/**
 * SnaggingListsDialog — iter-129
 *
 * Fullscreen module that lets Engineers / Admins capture and triage
 * site defects ("snags") during the reconstruction. Each snag carries:
 *   • title + description
 *   • severity (Minor / Major / Critical)
 *   • status   (Open / In Progress / Resolved / Won't Fix)
 *   • area link
 *   • assignee (free-text — kept simple for now)
 *   • due date
 *   • photos[] (re-uses the platform upload helper so we get auto
 *     compression + thumbnails for free)
 *   • comments thread
 *   • optional AI-suggested fix (Claude Sonnet via Emergent LLM key)
 *
 * Routes used:
 *   GET    /api/snags
 *   POST   /api/snags
 *   PATCH  /api/snags/{id}
 *   DELETE /api/snags/{id}
 *   POST   /api/snags/{id}/comments
 *   POST   /api/snags/{id}/ai-fix
 *
 * Only Engineer + Admin (and ownership / top_leader, which fold into
 * admin) can mutate. All signed-in users may read.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Plus, Search, Sparkles, Trash2, MessageSquarePlus, ImagePlus, Loader2, AlertTriangle,
} from 'lucide-react';
import { uploadWithProgress } from '../lib/upload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SEVERITIES = ['Minor', 'Major', 'Critical'];
const STATUSES = ['Open', 'In Progress', 'Resolved', "Won't Fix"];

const SEVERITY_COLORS = {
  Minor: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  Major: { bg: '#FED7AA', fg: '#9A3412', dot: '#EA580C' },
  Critical: { bg: '#FECACA', fg: '#991B1B', dot: '#DC2626' },
};
const STATUS_COLORS = {
  Open: { bg: '#DBEAFE', fg: '#1E40AF', dot: '#3B82F6' },
  'In Progress': { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  Resolved: { bg: '#DCFCE7', fg: '#166534', dot: '#22C55E' },
  "Won't Fix": { bg: '#E5E7EB', fg: '#374151', dot: '#6B7280' },
};

function abs(u) {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND}${u}`;
}

function Pill({ value, palette, ...rest }) {
  const p = palette[value] || { bg: '#E5E7EB', fg: '#374151', dot: '#6B7280' };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ background: p.bg, color: p.fg, fontFamily: 'Manrope' }}
      {...rest}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.dot }} />
      {value}
    </span>
  );
}

export default function SnaggingListsDialog({ open, onClose, token, currentRole, areas = [] }) {
  const [snags, setSnags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const canWrite = currentRole === 'engineer' || currentRole === 'admin'
    || currentRole === 'ownership' || currentRole === 'top_leader';
  const auth = useMemo(() => (token
    ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
    : { withCredentials: true }), [token]);

  const refresh = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      if (areaFilter) params.area_id = areaFilter;
      if (query.trim()) params.q = query.trim();
      const { data } = await axios.get(`${API}/snags`, { ...auth, params });
      setSnags(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load snagging list');
    } finally {
      setLoading(false);
    }
  }, [open, auth, statusFilter, severityFilter, areaFilter, query]);

  useEffect(() => { refresh(); }, [refresh]);

  // Close on Escape — but only when no detail/create sheet is open so
  // users don't accidentally bail out of the form they're filling in.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape' && !selectedId && !creating) onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, selectedId, creating, onClose]);

  const selected = useMemo(
    () => snags.find((s) => s.id === selectedId) || null,
    [snags, selectedId],
  );

  const handleCreate = async (draft) => {
    try {
      const { data } = await axios.post(`${API}/snags`, draft, auth);
      setSnags((prev) => [data, ...prev]);
      setCreating(false);
      toast.success('Snag created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    }
  };

  const handlePatch = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/snags/${id}`, patch, auth);
      setSnags((prev) => prev.map((s) => (s.id === id ? data : s)));
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
      return null;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this snag permanently?')) return;
    try {
      await axios.delete(`${API}/snags/${id}`, auth);
      setSnags((prev) => prev.filter((s) => s.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success('Snag deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleComment = async (id, text) => {
    try {
      const { data } = await axios.post(`${API}/snags/${id}/comments`, { text }, auth);
      setSnags((prev) => prev.map((s) => (
        s.id === id ? { ...s, comments: [...(s.comments || []), data] } : s
      )));
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Comment failed');
      return null;
    }
  };

  const handleAiFix = async (id) => {
    try {
      const { data } = await axios.post(`${API}/snags/${id}/ai-fix`, {}, auth);
      setSnags((prev) => prev.map((s) => (
        s.id === id ? { ...s, ai_suggested_fix: data.ai_suggested_fix } : s
      )));
      toast.success('AI suggestion generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI fix failed');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ background: '#F4F8F8', fontFamily: 'Manrope' }}
      data-testid="snagging-dialog"
    >
      {/* Header */}
      <header
        className="flex-shrink-0 px-4 sm:px-8 py-4 flex items-center gap-3 sm:gap-6"
        style={{ background: '#0A1A2F', color: 'white' }}
      >
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: '#A3D4D4' }}>
            Quality Control
          </p>
          <h1
            className="text-xl sm:text-2xl italic leading-tight truncate"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Snagging Lists
            <span className="ml-2 text-base" style={{ color: '#A3D4D4' }}>
              · {snags.length} item{snags.length === 1 ? '' : 's'}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              data-testid="snag-new-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
              style={{ background: '#22C55E', color: 'white' }}
            >
              <Plus className="w-3.5 h-3.5" /> New snag
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="snag-close-btn"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full transition-all hover:scale-110 active:scale-90 hover:bg-red-50"
            style={{ background: 'white', color: '#0A1A2F' }}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Filter bar */}
      <div
        className="flex-shrink-0 px-4 sm:px-8 py-3 flex flex-wrap items-center gap-2 border-b"
        style={{ background: 'white', borderColor: '#DCE5E5' }}
      >
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#7A8C8C' }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, area, description…"
            data-testid="snag-search-input"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg focus:outline-none"
            style={{ background: '#F4F8F8', color: '#0A1A2F', border: '1px solid #DCE5E5' }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="snag-filter-status"
          className="px-3 py-2 text-xs font-bold rounded-lg"
          style={{ background: '#F4F8F8', color: '#0A1A2F', border: '1px solid #DCE5E5' }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          data-testid="snag-filter-severity"
          className="px-3 py-2 text-xs font-bold rounded-lg"
          style={{ background: '#F4F8F8', color: '#0A1A2F', border: '1px solid #DCE5E5' }}
        >
          <option value="">All severities</option>
          {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={areaFilter}
          onChange={(e) => setAreaFilter(e.target.value)}
          data-testid="snag-filter-area"
          className="px-3 py-2 text-xs font-bold rounded-lg"
          style={{ background: '#F4F8F8', color: '#0A1A2F', border: '1px solid #DCE5E5' }}
        >
          <option value="">All areas</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
        {loading ? (
          <p className="text-center py-8 text-sm" style={{ color: '#7A8C8C' }}>Loading…</p>
        ) : snags.length === 0 ? (
          <div className="text-center py-16" data-testid="snag-empty">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: '#A3D4D4' }} />
            <p className="text-sm" style={{ color: '#7A8C8C' }}>
              No snags yet.
              {canWrite ? ' Click "New snag" to log the first one.' : ''}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-w-7xl mx-auto">
            {snags.map((s) => (
              <SnagCard
                key={s.id}
                snag={s}
                onOpen={() => setSelectedId(s.id)}
              />
            ))}
          </div>
        )}
      </div>

      {creating && (
        <SnagEditor
          areas={areas}
          onSave={handleCreate}
          onCancel={() => setCreating(false)}
          token={token}
        />
      )}
      {selected && (
        <SnagDetail
          snag={selected}
          areas={areas}
          canWrite={canWrite}
          token={token}
          onClose={() => setSelectedId(null)}
          onPatch={(p) => handlePatch(selected.id, p)}
          onDelete={() => handleDelete(selected.id)}
          onComment={(t) => handleComment(selected.id, t)}
          onAiFix={() => handleAiFix(selected.id)}
        />
      )}
    </div>
  );
}

function SnagCard({ snag, onOpen }) {
  const firstPhoto = (snag.photos || [])[0];
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`snag-card-${snag.id}`}
      className="text-left rounded-2xl overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-lg"
      style={{ background: 'white', border: '1px solid #DCE5E5' }}
    >
      {firstPhoto && (
        <div className="aspect-[16/9] overflow-hidden" style={{ background: '#0A1A2F' }}>
          <img src={abs(firstPhoto)} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Pill value={snag.severity} palette={SEVERITY_COLORS} />
          <Pill value={snag.status} palette={STATUS_COLORS} />
          {snag.area_name && (
            <span
              className="text-[10px] px-2 py-0.5 rounded-full"
              style={{ background: '#E0F2FE', color: '#075985' }}
            >
              {snag.area_name}
            </span>
          )}
        </div>
        <h3 className="text-sm font-bold leading-snug" style={{ color: '#0A1A2F' }}>
          {snag.title}
        </h3>
        {snag.description && (
          <p className="text-xs leading-relaxed line-clamp-2" style={{ color: '#4A5D5D' }}>
            {snag.description}
          </p>
        )}
        <div className="text-[10px] flex items-center gap-3" style={{ color: '#7A8C8C' }}>
          {snag.assignee_name && <span>👤 {snag.assignee_name}</span>}
          {snag.due_date && <span>📅 {snag.due_date}</span>}
          {(snag.comments?.length || 0) > 0 && (
            <span>💬 {snag.comments.length}</span>
          )}
        </div>
      </div>
    </button>
  );
}

function SnagEditor({ initial, onSave, onCancel, areas, token }) {
  const isEdit = !!initial;
  const [draft, setDraft] = useState(() => ({
    title: initial?.title || '',
    description: initial?.description || '',
    area_id: initial?.area_id || '',
    area_name: initial?.area_name || '',
    severity: initial?.severity || 'Minor',
    status: initial?.status || 'Open',
    assignee_name: initial?.assignee_name || '',
    due_date: initial?.due_date || '',
    photos: initial?.photos || [],
  }));
  const [uploading, setUploading] = useState(false);

  const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const updateArea = (id) => {
    const a = areas.find((x) => x.id === id);
    setDraft((d) => ({ ...d, area_id: id, area_name: a?.name || '' }));
  };

  const handleFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const next = [...draft.photos];
    for (const f of files) {
      try {
        const data = await uploadWithProgress(f, token);
        if (data?.url) next.push(data.url);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload failed for ${f.name}`);
      }
    }
    setUploading(false);
    update('photos', next);
    e.target.value = '';
  };

  const handleSubmit = () => {
    if (!draft.title.trim()) {
      toast.error('Title is required');
      return;
    }
    onSave(draft);
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: 'rgba(10,26,47,0.7)' }}
      data-testid="snag-editor"
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" style={{ background: 'white' }}>
        <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: '#DCE5E5' }}>
          <h2 className="text-lg font-bold" style={{ color: '#0A1A2F' }}>
            {isEdit ? 'Edit snag' : 'New snag'}
          </h2>
          <button type="button" onClick={onCancel} data-testid="snag-editor-cancel" className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Title *</span>
            <input
              type="text"
              value={draft.title}
              onChange={(e) => update('title', e.target.value)}
              data-testid="snag-editor-title"
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
              style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
            />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Description</span>
            <textarea
              rows={3}
              value={draft.description}
              onChange={(e) => update('description', e.target.value)}
              data-testid="snag-editor-description"
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
              style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Severity</span>
              <select
                value={draft.severity}
                onChange={(e) => update('severity', e.target.value)}
                data-testid="snag-editor-severity"
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
                style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
              >
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Status</span>
              <select
                value={draft.status}
                onChange={(e) => update('status', e.target.value)}
                data-testid="snag-editor-status"
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
                style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Area</span>
              <select
                value={draft.area_id}
                onChange={(e) => updateArea(e.target.value)}
                data-testid="snag-editor-area"
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
                style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
              >
                <option value="">— No area —</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Due date</span>
              <input
                type="date"
                value={draft.due_date}
                onChange={(e) => update('due_date', e.target.value)}
                data-testid="snag-editor-due"
                className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
                style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Assignee</span>
            <input
              type="text"
              value={draft.assignee_name}
              onChange={(e) => update('assignee_name', e.target.value)}
              data-testid="snag-editor-assignee"
              placeholder="e.g. TLDI Site Foreman"
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
              style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
            />
          </label>
          <div>
            <span className="text-xs font-bold text-slate-600">Photos</span>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {draft.photos.map((p, i) => (
                <div key={`${p}-${i}`} className="relative aspect-square rounded-lg overflow-hidden" style={{ background: '#0A1A2F' }}>
                  <img src={abs(p)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => update('photos', draft.photos.filter((_, idx) => idx !== i))}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.6)', color: 'white' }}
                    aria-label="Remove photo"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <label
                className="aspect-square rounded-lg flex flex-col items-center justify-center cursor-pointer text-xs font-bold"
                style={{ background: '#F4F8F8', border: '2px dashed #A3D4D4', color: '#4A5D5D' }}
                data-testid="snag-editor-photo-add"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ImagePlus className="w-5 h-5 mb-1" />
                    Add
                  </>
                )}
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFilePick} />
              </label>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2 border-t" style={{ borderColor: '#DCE5E5' }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="snag-editor-cancel-bottom"
            className="px-4 py-2 text-sm font-bold rounded-lg"
            style={{ background: '#F4F8F8', color: '#4A5D5D' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            data-testid="snag-editor-save"
            className="px-4 py-2 text-sm font-bold rounded-lg text-white"
            style={{ background: '#1F4FBE' }}
          >
            {isEdit ? 'Save changes' : 'Create snag'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SnagDetail({
  snag, areas, canWrite, token, onClose, onPatch, onDelete, onComment, onAiFix,
}) {
  const [editing, setEditing] = useState(false);
  const [comment, setComment] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const handleStatus = async (next) => {
    if (next === snag.status) return;
    await onPatch({ status: next });
  };
  const handleSeverity = async (next) => {
    if (next === snag.severity) return;
    await onPatch({ severity: next });
  };
  const handleCommentSubmit = async () => {
    const text = comment.trim();
    if (!text) return;
    const ok = await onComment(text);
    if (ok) setComment('');
  };
  const handleAi = async () => {
    setAiBusy(true);
    try { await onAiFix(); } finally { setAiBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: 'rgba(10,26,47,0.7)' }}
      data-testid="snag-detail"
    >
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl" style={{ background: 'white' }}>
        <div className="px-6 py-4 flex items-start justify-between border-b" style={{ borderColor: '#DCE5E5' }}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <Pill value={snag.severity} palette={SEVERITY_COLORS} />
              <Pill value={snag.status} palette={STATUS_COLORS} />
              {snag.area_name && (
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                  style={{ background: '#E0F2FE', color: '#075985' }}
                >
                  {snag.area_name}
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold leading-snug" style={{ color: '#0A1A2F' }}>{snag.title}</h2>
            <p className="text-[10px] mt-1" style={{ color: '#7A8C8C' }}>
              Created by {snag.created_by_name || 'unknown'} · {new Date(snag.created_at).toLocaleDateString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="snag-detail-close"
            className="flex-shrink-0 ml-3 text-slate-400 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {snag.description && (
            <p className="text-sm leading-relaxed" style={{ color: '#1A2E2E' }}>{snag.description}</p>
          )}

          {(snag.photos || []).length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {snag.photos.map((p) => (
                <a
                  key={p}
                  href={abs(p)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square rounded-lg overflow-hidden"
                  style={{ background: '#0A1A2F' }}
                >
                  <img src={abs(p)} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}

          {canWrite && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-bold text-slate-600">Status</span>
                <select
                  value={snag.status}
                  onChange={(e) => handleStatus(e.target.value)}
                  data-testid="snag-detail-status"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
                  style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-bold text-slate-600">Severity</span>
                <select
                  value={snag.severity}
                  onChange={(e) => handleSeverity(e.target.value)}
                  data-testid="snag-detail-severity"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg"
                  style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
                >
                  {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
          )}

          {/* AI suggestion */}
          <div
            className="rounded-xl p-4 border"
            style={{ background: '#FAF5FF', borderColor: '#E9D5FF' }}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className="text-xs font-bold inline-flex items-center gap-1.5" style={{ color: '#6B21A8' }}>
                <Sparkles className="w-3.5 h-3.5" /> AI-suggested fix
              </h3>
              {canWrite && (
                <button
                  type="button"
                  onClick={handleAi}
                  disabled={aiBusy}
                  data-testid="snag-ai-fix-btn"
                  className="text-[10px] font-bold px-2.5 py-1 rounded-lg text-white disabled:opacity-60"
                  style={{ background: '#7C3AED' }}
                >
                  {aiBusy ? 'Working…' : snag.ai_suggested_fix ? 'Regenerate' : 'Generate'}
                </button>
              )}
            </div>
            {snag.ai_suggested_fix ? (
              <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: '#1A2E2E' }}>
                {snag.ai_suggested_fix}
              </p>
            ) : (
              <p className="text-xs italic" style={{ color: '#6B21A8' }}>
                {canWrite
                  ? 'No suggestion yet — click "Generate" for an AI remediation plan.'
                  : 'No AI suggestion has been generated for this snag yet.'}
              </p>
            )}
          </div>

          {/* Comments */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#4A5D5D' }}>
              Comments
            </h3>
            <div className="space-y-2 mb-3">
              {(snag.comments || []).length === 0 && (
                <p className="text-xs italic" style={{ color: '#7A8C8C' }}>
                  No comments yet.
                </p>
              )}
              {(snag.comments || []).map((c) => (
                <div
                  key={c.id}
                  className="px-3 py-2 rounded-lg text-xs"
                  style={{ background: '#F4F8F8' }}
                  data-testid={`snag-comment-${c.id}`}
                >
                  <p className="font-bold" style={{ color: '#0A1A2F' }}>
                    {c.author_name || 'Unknown'}
                    <span className="ml-2 font-normal text-[10px]" style={{ color: '#7A8C8C' }}>
                      {new Date(c.created_at).toLocaleString()}
                    </span>
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap" style={{ color: '#1A2E2E' }}>{c.text}</p>
                </div>
              ))}
            </div>
            {canWrite && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCommentSubmit(); }}
                  placeholder="Add a comment…"
                  data-testid="snag-comment-input"
                  className="flex-1 px-3 py-2 text-sm rounded-lg"
                  style={{ background: '#F4F8F8', border: '1px solid #DCE5E5' }}
                />
                <button
                  type="button"
                  onClick={handleCommentSubmit}
                  disabled={!comment.trim()}
                  data-testid="snag-comment-submit"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50"
                  style={{ background: '#1F4FBE' }}
                >
                  <MessageSquarePlus className="w-3.5 h-3.5" /> Post
                </button>
              </div>
            )}
          </div>
        </div>

        {canWrite && (
          <div
            className="px-6 py-4 flex items-center justify-between border-t"
            style={{ borderColor: '#DCE5E5' }}
          >
            <button
              type="button"
              onClick={onDelete}
              data-testid="snag-detail-delete"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg"
              style={{ background: '#FECACA', color: '#991B1B' }}
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete snag
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="snag-detail-edit"
              className="px-4 py-2 text-xs font-bold rounded-lg text-white"
              style={{ background: '#1F4FBE' }}
            >
              Edit details
            </button>
          </div>
        )}
      </div>

      {editing && (
        <SnagEditor
          initial={snag}
          areas={areas}
          token={token}
          onCancel={() => setEditing(false)}
          onSave={async (draft) => {
            await onPatch(draft);
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}
